'use client';

import React, { useEffect, useState, useRef } from 'react';
import { 
  Check, CheckCircle2, Bell, Loader2, 
  AlertTriangle, CreditCard, Gift, ShieldAlert, ArrowRight
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useMainModal } from '@/components/MainModalProvider';

interface Notification {
  id: number;
  userId: number;
  type: string; 
  title: string;
  content: string; 
  fullContent?: string; 
  actionUrl?: string; 
  isRead: boolean;
  createdAt: string;
}

interface InboxDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  onMarkAsReadSuccess?: () => void;
  inline?: boolean;
  triggerRef?: { current: HTMLButtonElement | null };
}

const LOCAL_API_URL = process.env.NEXT_PUBLIC_LOCAL_API || 'https://sg-public-api.royolands.com/rpweb';

export default function InboxDropdown({ isOpen, onClose, user, onMarkAsReadSuccess, inline = false, triggerRef }: InboxDropdownProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMarking, setIsMarking] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const router = useRouter();
  const { openModal } = useMainModal();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        !triggerRef?.current?.contains(target)
      ) {
        onClose();
      }
    };
    if (isOpen && !inline) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, inline, onClose, triggerRef]);

  useEffect(() => {
    if (isOpen && user?.oauthId) fetchNotifications();
  }, [isOpen, user]);

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${LOCAL_API_URL}/api/inbox/notifications/${user.oauthId}`, { credentials: 'include' });
      if (res.ok) setNotifications(await res.json());
    } catch (e) {
      console.error("Ошибка загрузки уведомлений:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const markAllAsRead = async () => {
    if (isMarking || notifications.every(n => n.isRead)) return;
    setIsMarking(true);
    try {
      const res = await fetch(`${LOCAL_API_URL}/api/inbox/notifications/${user.oauthId}/read`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        if (onMarkAsReadSuccess) onMarkAsReadSuccess();
      }
    } catch (e) {} finally {
      setIsMarking(false);
    }
  };

  const handleNotificationClick = (notif: Notification) => {
    onClose(); 

    if (notif.actionUrl) {
      router.push(notif.actionUrl);
      return;
    }

    let modalType: 'info' | 'success' | 'error' = 'info';
    if (notif.type === 'WARN' || notif.type === 'SYSTEM') modalType = 'error';
    if (notif.type === 'PAYMENT' || notif.type === 'GIFT') modalType = 'success';

    openModal({
      title: notif.title,
      message: notif.fullContent || notif.content, 
      type: modalType,
      confirmText: 'Закрыть',
      messageColor: 'text-zinc-300' 
    });
  };

  const getIconForType = (type: string, isRead: boolean) => {
    const colorClass = isRead ? 'text-zinc-500' : 'text-white';
    switch (type) {
      case 'PAYMENT': return <CreditCard size={18} className={isRead ? 'text-zinc-500' : 'text-emerald-400'} />;
      case 'WARN': return <AlertTriangle size={18} className={isRead ? 'text-zinc-500' : 'text-orange-400'} />;
      case 'SYSTEM': return <ShieldAlert size={18} className={isRead ? 'text-zinc-500' : 'text-red-400'} />;
      case 'GIFT': return <Gift size={18} className={isRead ? 'text-zinc-500' : 'text-purple-400'} />;
      default: return <Bell size={18} className={colorClass} />;
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diff < 60) return 'Только что';
    if (diff < 3600) return `${Math.floor(diff / 60)} мин. назад`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ч. назад`;
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  };

  const hasUnread = notifications.some(n => !n.isRead);

  const dropdown = (
    <div 
      ref={dropdownRef}
      className={`transition-all duration-300 ${
        inline
          ? 'relative w-full origin-top'
          : 'absolute top-full right-0 z-50 mt-4 w-[340px] origin-top-right sm:w-[380px]'
      } ${
        isOpen
          ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 scale-[0.98] -translate-y-2 pointer-events-none'
      }`}
    >
      <div className={`flex flex-col overflow-hidden ${
        inline
          ? 'rounded-2xl border border-[#232734] bg-[#11141d]'
          : 'rounded-2xl border border-white/10 bg-[#111111]/95 shadow-2xl backdrop-blur-xl'
      }`}>
        
        {/* Шапка (добавлен shrink-0, чтобы она не сжималась) */}
        <div className={`shrink-0 flex items-center justify-between px-4 py-3 border-b ${
          inline ? 'border-[#232734] bg-white/[0.03]' : 'border-white/5 bg-white/[0.02] px-5 py-4'
        }`}>
          <div className="flex items-center gap-2">
            <Bell size={18} className="text-white" />
            <h3 className="text-white font-semibold text-[15px]">Уведомления</h3>
          </div>
          {hasUnread && (
            <button 
              onClick={markAllAsRead}
              disabled={isMarking}
              className={`text-[12px] font-medium transition flex items-center gap-1 disabled:opacity-50 outline-none cursor-pointer ${
                inline ? 'text-[#c5c9d2] hover:text-white' : 'text-indigo-400 hover:text-indigo-300'
              }`}
            >
              {isMarking ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              Прочитать все
            </button>
          )}
        </div>

        {/* Список — добавлен data-lenis-prevent, чтобы Lenis не перехватывал скролл этого блока */}
        <div 
          data-lenis-prevent
          className="overflow-y-auto overscroll-contain max-h-[400px] min-h-[200px] relative [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/10 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-white/20"
        >
          
          {isLoading ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500 h-[200px]">
              <Loader2 size={24} className={`animate-spin mb-2 ${inline ? 'text-[#c5c9d2]' : 'text-indigo-500'}`} />
              <span className="text-sm">Загрузка...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500 px-6 text-center h-[200px]">
              <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center mb-3">
                <CheckCircle2 size={24} className="text-zinc-600" />
              </div>
              <span className="text-sm font-medium text-zinc-400">У вас нет новых уведомлений</span>
              <span className="text-xs mt-1">Здесь будут появляться важные сообщения и события.</span>
            </div>
          ) : (
            <div className="flex flex-col">
              {notifications.map((notif) => (
                <button
                  key={notif.id} 
                  onClick={() => handleNotificationClick(notif)}
                  className={`w-full text-left p-4 border-b border-white/5 transition hover:bg-white/5 group relative outline-none cursor-pointer flex gap-3 ${
                    notif.isRead ? 'opacity-70' : inline ? 'bg-white/[0.02]' : 'bg-indigo-500/[0.02]'
                  }`}
                >
                  {/* Индикатор непрочитанного */}
                  {!notif.isRead && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500 rounded-r-full" />
                  )}
                  
                  {/* Иконка типа */}
                  <div className="mt-1 flex-shrink-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${notif.isRead ? 'bg-white/5' : 'bg-white/10'}`}>
                      {getIconForType(notif.type, notif.isRead)}
                    </div>
                  </div>

                  {/* Контент */}
                  <div className="flex-1 min-w-0 pr-2">
                    <div className="flex justify-between items-start mb-1 gap-2">
                      <span className={`text-[14px] font-semibold leading-tight truncate ${notif.isRead ? 'text-zinc-300' : 'text-white'}`}>
                        {notif.title}
                      </span>
                      <span className="text-[11px] text-zinc-500 whitespace-nowrap mt-0.5">
                        {formatTime(notif.createdAt)}
                      </span>
                    </div>
                    
                    <p className="text-[13px] text-zinc-400 leading-snug line-clamp-2">
                      {notif.content}
                    </p>
                    
                    <div className={`flex items-center gap-1 mt-2 text-[12px] font-medium transition-colors ${
                      inline ? 'text-[#aeb4c0] group-hover:text-white' : 'text-indigo-400/80 group-hover:text-indigo-400'
                    }`}>
                      {notif.actionUrl ? (
                        <>Перейти <ArrowRight size={12} /></>
                      ) : (
                        'Подробнее'
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return dropdown;
}